import React, { useState, useEffect } from "react";

interface ChangeDescriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (combinedInput: string) => void;
    inputContent: string;
    title?: string;
    submitLabel?: string;
    cancelLabel?: string;
    placeholder?: string;
    className?: string;
}

export const ChangeDescriptionModal: React.FC<ChangeDescriptionModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    inputContent,
    title = "Describe Changes",
    submitLabel = "Submit",
    cancelLabel = "Cancel",
    placeholder = "Describe the changes you want...",
    className = "",
}) => {
    const [changeDescription, setChangeDescription] = useState("");

    // Reset change description when modal opens
    useEffect(() => {
        if (isOpen) {
            setChangeDescription("");
        }
    }, [isOpen]);

    const handleSubmit = () => {
        const combinedInput = `Current Content:\n${inputContent}\n\nRequested Changes:\n${changeDescription}`;
        onSubmit(combinedInput);
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            handleSubmit();
        }
    };

    if (!isOpen) return null;

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "24px",
        maxWidth: "600px",
        width: "100%",
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    };

    const headerStyle: React.CSSProperties = {
        marginBottom: "20px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "20px",
        fontWeight: "600",
        color: "#1f2937",
        margin: 0,
    };

    const contentStyle: React.CSSProperties = {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        minHeight: 0,
    };

    const fieldStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
    };

    const labelStyle: React.CSSProperties = {
        fontSize: "14px",
        fontWeight: "500",
        color: "#374151",
        marginBottom: "6px",
    };

    const readOnlyTextAreaStyle: React.CSSProperties = {
        width: "100%",
        height: "120px",
        padding: "12px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        fontFamily: "monospace",
        backgroundColor: "#f9fafb",
        color: "#6b7280",
        resize: "none",
        overflow: "auto",
    };

    const editableTextAreaStyle: React.CSSProperties = {
        width: "100%",
        height: "120px",
        padding: "12px",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        resize: "vertical",
        minHeight: "80px",
        outline: "none",
        transition: "border-color 0.2s",
    };

    const footerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
        marginTop: "20px",
        paddingTop: "16px",
        borderTop: "1px solid #e5e7eb",
    };

    const buttonStyle: React.CSSProperties = {
        padding: "10px 20px",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: "pointer",
        border: "none",
        transition: "all 0.2s",
        outline: "none",
    };

    const cancelButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        backgroundColor: "#f3f4f6",
        color: "#374151",
    };

    const submitButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        backgroundColor: "#3b82f6",
        color: "white",
    };

    const disabledButtonStyle: React.CSSProperties = {
        ...submitButtonStyle,
        backgroundColor: "#9ca3af",
        cursor: "not-allowed",
    };

    return (
        <div
            style={overlayStyle}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className={className}
                style={modalStyle}
                onKeyDown={handleKeyDown}
            >
                <div style={headerStyle}>
                    <h2 style={titleStyle}>{title}</h2>
                </div>

                <div style={contentStyle}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>Current Content:</label>
                        <textarea
                            style={readOnlyTextAreaStyle}
                            value={inputContent}
                            readOnly
                        />
                    </div>

                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Describe the changes you want:
                        </label>
                        <textarea
                            style={editableTextAreaStyle}
                            value={changeDescription}
                            onChange={(e) =>
                                setChangeDescription(e.target.value)
                            }
                            placeholder={placeholder}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = "#3b82f6";
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                            autoFocus
                        />
                    </div>
                </div>

                <div style={footerStyle}>
                    <button
                        style={cancelButtonStyle}
                        onClick={onClose}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#e5e7eb";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                        }}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        style={
                            changeDescription.trim()
                                ? submitButtonStyle
                                : disabledButtonStyle
                        }
                        onClick={handleSubmit}
                        disabled={!changeDescription.trim()}
                        onMouseEnter={(e) => {
                            if (changeDescription.trim()) {
                                e.currentTarget.style.backgroundColor =
                                    "#2563eb";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (changeDescription.trim()) {
                                e.currentTarget.style.backgroundColor =
                                    "#3b82f6";
                            }
                        }}
                    >
                        {submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};
